import { useCallback, useEffect, useState } from "react";

const APP_LOADING_EVENT_NAME = "appLoading";

/**
 * A hook that intercepts and manages the {@link AppLoadingEvent}.
 */
export const useAppLoadingEvent = () => {
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handler = (e: AppLoadingEvent) => {
      const _loading = e.detail.loading;
      setTimeout(() => {
        setLoading(_loading);
      });
    };
    document.addEventListener<"appLoading">(APP_LOADING_EVENT_NAME, handler);
    return () => {
      document.removeEventListener<"appLoading">(APP_LOADING_EVENT_NAME, handler);
    };
  }, []);

  return loading;
};

export interface AppLoaderOptions {
  readonly variant?: "bars" | "oval" | "dots";
}

/**
 * A hook that can be used to manually trigger the application loading state.
 *
 * The {@link useAppLoading} returns a method, that when called, will set the loading state (loading or not loading) on a loading indicator
 * in the top level structure of the application.  As such, this hook should not be used in conjunction with the {@link AppLoading}
 * component, but rather in place of it.
 *
 * @example
 * // Proper usage
 * const [setLoading] = useAppLoading();
 * const [data, setData] = useState([]);
 *
 * useEffect(() => {
 *   setLoading(true);
 *   api.makeRequest().then((response) => {
 *     setLoading(false)
 *     setData(response)
 *   })
 * }, [])
 *
 * return <MyComponent data=data />
 */
export const useAppLoading = (options?: AppLoaderOptions): [(v: boolean) => void] => {
  const variant = options?.variant || "oval";

  const setLoading = useCallback(
    (value: boolean, options?: AppLoaderOptions) => {
      const AppLoadingEvent: AppLoadingEvent = new CustomEvent(APP_LOADING_EVENT_NAME, { detail: { loading: value, variant, ...options } });
      document.dispatchEvent(AppLoadingEvent);
    },
    [variant],
  );

  useEffect(() => {
    return () => {
      const AppLoadingEvent: AppLoadingEvent = new CustomEvent(APP_LOADING_EVENT_NAME, { detail: { loading: false } });
      document.dispatchEvent(AppLoadingEvent);
    };
  }, []);

  return [setLoading];
};
